// React
import React, { useEffect } from 'react';

// Lexical Core
import { EditorState } from 'lexical';

// Lexical React Plugins
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

// Lexical Nodes
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { LinkNode, AutoLinkNode } from '@lexical/link';

// Lexical Markdown
import { TRANSFORMERS } from '@lexical/markdown';

// Local
import { editorTheme } from './theme/EditorTheme';
import { useLexicalContent } from '../../hooks/useLexicalContent';

interface LexicalEditorProps {
  initialContent?: string;
  onChange?: (content: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

// Lexical 에러 핸들러
const handleError = (error: Error) => {
  console.error('[Lexical Error]', error);
};

// 간단한 ErrorBoundary (타입 호환성 위해 커스텀)
const CustomErrorBoundary: React.FC<{
  children: React.ReactNode;
  onError: (error: Error) => void;
}> = ({ children }) => {
  return <>{children}</>;
};

// Lexical Editor 초기 설정
const createEditorConfig = (initialContent?: string) => ({
  namespace: 'FocusWriterEditor',
  theme: editorTheme,
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    CodeNode,
    CodeHighlightNode,
    LinkNode,
    AutoLinkNode,
    HorizontalRuleNode,
  ],
  onError: handleError,
  editable: true,
});

// 키보드 이벤트를 React 컴포넌트 외부로 전달하는 플러그인
const KeyboardPlugin: React.FC<{ onKeyDown?: (e: React.KeyboardEvent) => void }> = ({ onKeyDown }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onKeyDown) return;

    const handleKeyboardEvent = (event: KeyboardEvent) => {
      onKeyDown(event as any);
    };

    return editor.registerRootListener((rootElement) => {
      if (rootElement) {
        rootElement.addEventListener('keydown', handleKeyboardEvent);
        return () => rootElement.removeEventListener('keydown', handleKeyboardEvent);
      }
    });
  }, [editor, onKeyDown]);

  return null;
};

const LexicalEditor: React.FC<LexicalEditorProps> = ({
  initialContent,
  onChange,
  onKeyDown,
  placeholder = 'Start writing your story...',
  className = '',
  style = {},
}) => {
  const editorConfig = createEditorConfig(initialContent);

  // ContentLoader 컴포넌트: initialContent를 에디터에 로드
  const ContentLoader: React.FC<{ content: string }> = ({ content }) => {
    const [editor] = useLexicalComposerContext();
    const { setContent } = useLexicalContent(editor);

    useEffect(() => {
      if (content) {
        setContent(content);
      }
    }, [content, setContent]);

    return null;
  };

  // EditorState 변경 시 plain text로 변환하여 외부로 전달
  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const textContent = editorState.read(() => {
        const root = editorState._nodeMap.get('root');
        return root ? (root as any).getTextContent() : '';
      });
      onChange?.(textContent);
    });
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="relative">
        {initialContent && <ContentLoader content={initialContent} />}
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={className}
              style={style}
              aria-label="Text editor"
            />
          }
          placeholder={
            <div
              className="absolute top-0 left-0 p-8 pointer-events-none opacity-50"
              style={style}
            >
              {placeholder}
            </div>
          }
          ErrorBoundary={CustomErrorBoundary}
        />
        <HistoryPlugin delay={300} />
        <OnChangePlugin onChange={handleChange} />
        <KeyboardPlugin onKeyDown={onKeyDown} />
        <ListPlugin />
        <LinkPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      </div>
    </LexicalComposer>
  );
};

export default LexicalEditor;
